import React, { useState } from "react";
import {
  Grid,
  TextField,
  Paper,
  Typography,
  Box,
  IconButton,
  Tooltip,
  Chip,
  Divider,
  InputAdornment
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import EditIcon from "@mui/icons-material/Edit";
import BuildIcon from "@mui/icons-material/Build";
import CategoryIcon from "@mui/icons-material/Category";
import { TECHNICAL_SPECS_BY_CATEGORY } from "../utils/assetConstants";
import { generateUniqueKey } from "../utils/assetHelpers";

const AssetTechnicalSpecs = ({ asset, onChange }) => {
  const technicalSpecs = asset.technical_specifications || {};
  
  // Логирование для отладки
  console.log('🔧 AssetTechnicalSpecs рендер');
  console.log('🔧 Текущие technical_specifications:', technicalSpecs);
  console.log('🔧 Количество характеристик:', Object.keys(technicalSpecs).length);

  const handleSpecChange = (key, value) => {
    console.log('📝 Изменение характеристики:', { key, value });
    const updatedSpecs = {
      ...asset.technical_specifications,
      [key]: value
    };
    onChange('technical_specifications', updatedSpecs);
    console.log('✅ Обновленные характеристики:', updatedSpecs);
  };

  const addSpecification = () => {
    const existingKeys = Object.keys(technicalSpecs);
    const newKey = generateUniqueKey(existingKeys, "новый_параметр");
    handleSpecChange(newKey, "");
  };

  const addTechnicalSpec = () => {
    const existingKeys = Object.keys(technicalSpecs);
    const newKey = generateUniqueKey(existingKeys, "новый_параметр");
    handleSpecChange(newKey, "");
  };

  const quickAddSpec = (specName) => {
    const existingKeys = Object.keys(technicalSpecs);
    const newKey = generateUniqueKey(existingKeys, specName);
    handleSpecChange(newKey, "");
  };

  const removeTechnicalSpec = (key) => {
    const newSpecs = { ...technicalSpecs };
    delete newSpecs[key];
    onChange('technical_specifications', newSpecs);
  };

  const renameTechnicalSpecKey = (oldKey, newKey) => {
    if (oldKey === newKey || !newKey.trim()) return;

    const existingKeys = Object.keys(technicalSpecs);
    const finalKey = existingKeys.includes(newKey)
      ? generateUniqueKey(existingKeys, newKey)
      : newKey;

    const newSpecs = { ...technicalSpecs };
    newSpecs[finalKey] = newSpecs[oldKey];
    delete newSpecs[oldKey];

    onChange('technical_specifications', newSpecs);
  };

  const addCategorySpecs = () => {
    const categorySpecs = TECHNICAL_SPECS_BY_CATEGORY[asset.category] || [];
    const existingKeys = Object.keys(technicalSpecs);
    const newSpecs = { ...technicalSpecs };

    categorySpecs.forEach(spec => {
      if (!existingKeys.includes(spec.key)) {
        newSpecs[spec.key] = "";
      }
    });

    onChange('technical_specifications', newSpecs);
  };

  const categorySpecs = TECHNICAL_SPECS_BY_CATEGORY[asset.category] || [];

  return (
    <Paper elevation={3} sx={{ p: 1.5, mb: 2, minHeight: 220 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Box display="flex" alignItems="center">
          <BuildIcon sx={{ mr: 1, color: 'primary.main' }} />
          <Typography variant="h6" component="h2">
            Технические характеристики
          </Typography>
        </Box>
        <Box display="flex" gap={1}>
          {asset.category && categorySpecs.length > 0 && (
            <Tooltip title={`Добавить стандартные характеристики для категории "${asset.category}"`}>
              <Chip
                icon={<CategoryIcon fontSize="small" />}
                label={`Шаблон: ${asset.category}`}
                onClick={addCategorySpecs}
                color="primary"
                variant="outlined"
                size="medium"
                clickable
                sx={{
                  borderStyle: 'dashed',
                  '&:hover': {
                    backgroundColor: 'primary.lighter',
                    borderStyle: 'solid'
                  }
                }}
              />
            </Tooltip>
          )}
          <Tooltip title="Добавить новую характеристику">
            <IconButton
              onClick={addTechnicalSpec}
              color="primary"
              sx={{
                border: 1,
                borderColor: 'primary.main',
                '&:hover': {
                  backgroundColor: 'primary.lighter'
                }
              }}
            >
              <AddCircleOutlineIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      <Divider sx={{ mb: 2 }} />

      <Grid container spacing={3}>
        {Object.entries(technicalSpecs).map(([key, value]) => {
          const categorySpec = categorySpecs.find(spec => spec.key === key);
          const fieldType = categorySpec?.type || "text";
          const fieldLabel = categorySpec?.label || key;
          const fieldUnit = categorySpec?.unit || "";

          return (
            <Grid item xs={12} sm={6} md={4} key={key}>
              <Paper
                variant="outlined"
                sx={{
                  p: 1.2,
                  height: '110px',
                  transition: 'all 0.3s',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'flex-start',
                  '&:hover': {
                    boxShadow: 2,
                    borderColor: 'primary.main'
                  }
                }}
              >
                <Box display="flex" flexDirection="column" gap={0.5}>
                  <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
                    <Typography variant="caption" color="text.secondary">
                      {fieldLabel}
                    </Typography>
                    <Box display="flex" gap={0.5}>
                      <Tooltip title="Переименовать">
                        <IconButton
                          size="small"
                          onClick={() => {
                            const newKey = prompt("Новое название характеристики:", key);
                            if (newKey) {
                              renameTechnicalSpecKey(key, newKey);
                            }
                          }}
                          sx={{ p: 0.5 }}
                        >
                          <EditIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Удалить">
                        <IconButton
                          size="small"
                          onClick={() => removeTechnicalSpec(key)}
                          color="error"
                          sx={{ p: 0.5 }}
                        >
                          <DeleteIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </Box>
                  <TextField
                    fullWidth
                    type={fieldType}
                    value={value ?? ""}
                    inputProps={{ 'aria-label': fieldLabel }}
                    onChange={(e) => handleSpecChange(key, e.target.value)}
                    size="small"
                    InputProps={{
                      endAdornment: fieldUnit ? (
                        <InputAdornment position="end">
                          <Typography variant="caption" color="text.secondary">
                            {fieldUnit}
                          </Typography>
                        </InputAdornment>
                      ) : null,
                    }}
                    sx={{
                      '& .MuiInputBase-input': {
                        fontSize: '0.95rem',
                        fontWeight: 500
                      }
                    }}
                  />
                </Box>
              </Paper>
            </Grid>
          );
        })}

        {Object.keys(technicalSpecs).length === 0 && (
          <Grid item xs={12}>
            <Paper
              variant="outlined"
              sx={{
                p: 4,
                textAlign: 'center',
                borderStyle: 'dashed',
                borderColor: 'divider',
                backgroundColor: 'grey.50'
              }}
            >
              <BuildIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
              <Typography variant="body1" color="text.secondary" gutterBottom>
                Технические характеристики не добавлены
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Нажмите кнопку "+" чтобы добавить характеристику
                {asset.category && categorySpecs.length > 0 && (
                  <> или используйте шаблон для категории</>
                )}
              </Typography>
            </Paper>
          </Grid>
        )}
      </Grid>
    </Paper>
  );
};

export default AssetTechnicalSpecs;
